'use client';

import classNames from 'classnames';
import { type ReactNode, useMemo } from 'react';
import type CardLocation from '@alltrails/amplitude/enums/CardLocation';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import logTrailDetailsNearbyTrailClicked from '@alltrails/amplitude/events/logTrailDetailsNearbyTrailClicked';
import TrailStandardCard from '@alltrails/cards/components/TrailStandardCard';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useMediaQuery from '@alltrails/core/hooks/useMediaQuery';
import Carousel from '@alltrails/denali/components/Carousel';
import Heading from '@alltrails/denali/components/Heading';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { BREAKPOINT_MD_SIZE } from '@alltrails/denali/tokens';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import type Trail from '@alltrails/types/Trail';
import { AlgoliaTrail } from '../..';
import trailFromAlgoliaTrail from '../../utils/trailFromAlgoliaTrail';
import styles from './TrailsCarousel.module.scss';

const reduxListsAnalyticsInfo = (isTrailPage: boolean) =>
  isTrailPage
    ? {
        detailedCardLocation: 'nearby trail card' as CardLocation,
        page: PageStrings.TRAIL_PAGE
      }
    : {
        detailedCardLocation: 'location page' as CardLocation,
        page: PageStrings.SEO_PAGE
      };

export type TrailsCarouselProps = {
  header: ReactNode;
  isBot?: boolean;
  mainTrailId?: number;
  page_type?: 'trail' | 'location';
  trails: AlgoliaTrail[];
};

const TrailsCarousel = ({ header, isBot = false, mainTrailId, page_type = 'trail', trails }: TrailsCarouselProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const isMediumDown = useMediaQuery(`(max-width: ${BREAKPOINT_MD_SIZE}px)`);
  const isTrailPage = page_type === 'trail';
  const analyticsInfo = useMemo(() => reduxListsAnalyticsInfo(isTrailPage), [isTrailPage]);
  const { handleFavoriteClick, isComplete, isFavorite, isVerified, listModal, signUpModal } = useReduxListItems(
    undefined,
    false /* preventAutoInitialize */,
    analyticsInfo
  );
  const isMobile = useIsMobileSizedScreen();

  const onTrailClick = (trail: Trail) => {
    if (isTrailPage && mainTrailId) {
      logTrailDetailsNearbyTrailClicked({ trail_id: mainTrailId, destination_trail_id: trail.id });
    }
    logTrailCardClicked({
      card_location: isTrailPage ? 'web trail page' : 'location page',
      detailed_card_location: isTrailPage ? 'web trail page top module' : 'location page',
      trail_id: String(trail.id)
    });
  };

  const trailsList = isBot ? trails : trails.slice(0, 12);

  return (
    <section>
      <Heading as="h2" className={styles.sectionTitle} size="400">
        {header}
      </Heading>
      {isBot ? (
        <div className={styles.trailsCarouselBotContainer}>
          {trailsList.map(trail => {
            const renderableTrail = trailFromAlgoliaTrail(trail);
            return (
              <TrailStandardCard
                key={trail.id}
                isCompleted={isComplete('trail', renderableTrail.id)}
                isFavorite={isFavorite('trail', renderableTrail.id)}
                isPhotoZoomEnabled
                isVerified={isVerified('trail', renderableTrail.id)}
                languageRegionCode={languageRegionCode}
                maxPhotos={1}
                onCardClick={() => onTrailClick(renderableTrail)}
                onFavoriteClick={() => handleFavoriteClick('trail', renderableTrail.id)}
                trail={renderableTrail}
              />
            );
          })}
        </div>
      ) : (
        <Carousel
          className={classNames({ [styles.mobileCarousel]: isMobile })}
          getItemKey={trail => `${trail.ID}`}
          items={trailsList}
          renderItem={item => {
            const trail = trailFromAlgoliaTrail(item);
            return (
              <TrailStandardCard
                key={trail.id}
                isCompleted={isComplete('trail', trail.id)}
                isFavorite={isFavorite('trail', trail.id)}
                isPhotoZoomEnabled
                isVerified={isVerified('trail', trail.id)}
                languageRegionCode={languageRegionCode}
                maxPhotos={1}
                onCardClick={() => onTrailClick(trail)}
                onFavoriteClick={() => handleFavoriteClick('trail', trail.id)}
                trail={trail}
              />
            );
          }}
          showOverflow={isMediumDown}
          showPaginationDots={false}
          slidesPerViewBreakpoints={{ 541: 2, 1121: 3 }}
          slidesPerViewMobile={1}
        />
      )}
      {listModal}
      {signUpModal}
    </section>
  );
};

export default TrailsCarousel;
